import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MdLocalShipping, MdAdd, MdDelete, MdSave, MdEdit, MdLocationOn } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../shared/Toast';

type ChargeRow = { label: string; amount: string; chargeType: 'shipping' | 'packing' | 'custom' | 'other' };
type TaxRow = { label: string; rate: string; amount: string };

const emptyCharge = (): ChargeRow => ({ label: '', amount: '', chargeType: 'custom' });
const emptyTax = (): TaxRow => ({ label: '', rate: '', amount: '' });

/** Tax is applied to line items only — not shipping/packing charges. */
function computeItemsSubtotal(po: any): number {
    const fromShipping = Number(po?.shippingDetails?.itemsSubtotal);
    if (Number.isFinite(fromShipping) && fromShipping > 0) return fromShipping;
    const items = Array.isArray(po?.items) ? po.items : [];
    if (items.length === 0) return Number(po?.totalAmount) || 0;
    return items.reduce(
        (sum: number, item: any) =>
            sum + (Number(item.total) || Number(item.quantity) * Number(item.unitPrice) || 0),
        0
    );
}

function DeliveryAddressCard({ po }: { po: any }) {
    const a = po?.shippingAddress;
    if (!a?.street && !a?.city && !a?.country) {
        return (
            <div className="mx-6 mt-4 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm">
                <p className="font-bold flex items-center gap-2"><MdLocationOn className="w-4 h-4" /> Delivery address pending</p>
                <p className="mt-1 text-xs">Customer has not shared a delivery destination yet. Charges can still be drafted, but wait for the address before finalizing estimates.</p>
            </div>
        );
    }
    const line2 = [a.city, a.state, a.zipCode].filter(Boolean).join(', ');
    return (
        <div className="mx-6 mt-4 p-4 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-950 text-sm space-y-1">
            <p className="font-bold flex items-center gap-2"><MdLocationOn className="w-4 h-4" /> Delivery destination</p>
            <p className="font-semibold">{a.street}</p>
            {line2 && <p>{line2}</p>}
            {a.country && <p>{a.country}</p>}
            {(a.contactName || a.contactPhone || a.contactEmail) && (
                <p className="text-xs pt-1 text-emerald-800">
                    {[a.contactName, a.contactPhone, a.contactEmail].filter(Boolean).join(' · ')}
                </p>
            )}
            <p className="text-[11px] text-emerald-800/80 pt-1">Use this address to estimate shipping / packing charges for this PO.</p>
        </div>
    );
}

interface AdminShippingChargesSectionProps {
    po: any;
    poId: string;
    prefill?: {
        charges: ChargeRow[];
        taxes: TaxRow[];
        notes?: string;
    } | null;
    onPrefillApplied?: () => void;
    highlightCustomerRequest?: boolean;
}

function hasPersistedCharges(details: any): boolean {
    const charges = Array.isArray(details?.vendorCharges) ? details.vendorCharges : [];
    const taxes = Array.isArray(details?.taxes) ? details.taxes : [];
    return (
        charges.some((c: any) => String(c?.label || '').trim()) ||
        taxes.some((t: any) => String(t?.label || '').trim())
    );
}

export function AdminShippingChargesSection({
    po,
    poId,
    prefill,
    onPrefillApplied,
    highlightCustomerRequest = false,
}: AdminShippingChargesSectionProps) {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const shippingMode = po.shippingMode as 'vendor' | 'self' | null | undefined;
    const details = po.shippingDetails || {};
    const shippingLocked = !!po.shippingModeConfirmed;
    const canEdit =
        shippingMode !== 'self' &&
        !['cancelled', 'completed'].includes(po.status);
    const saved = hasPersistedCharges(details);

    const [isEditing, setIsEditing] = useState(!saved);
    const [charges, setCharges] = useState<ChargeRow[]>([emptyCharge()]);
    const [taxes, setTaxes] = useState<TaxRow[]>([emptyTax()]);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        const existingCharges = details.vendorCharges?.length
            ? details.vendorCharges.map((c: any) => ({
                label: c.label || '',
                amount: String(c.amount ?? ''),
                chargeType: c.chargeType || 'custom',
            }))
            : [emptyCharge()];
        const existingTaxes = details.taxes?.length
            ? details.taxes.map((t: any) => ({
                label: t.label || '',
                rate: t.rate !== undefined ? String(t.rate) : '',
                amount: String(t.amount ?? ''),
            }))
            : [emptyTax()];
        setCharges(existingCharges);
        setTaxes(existingTaxes);
        setNotes(details.notes || '');
        if (hasPersistedCharges(details)) {
            setIsEditing(false);
        }
    }, [po._id, details.vendorCharges, details.taxes, details.notes]);

    useEffect(() => {
        if (!prefill) return;
        if (prefill.charges.length > 0) {
            setCharges(prefill.charges);
        }
        if (prefill.taxes.length > 0) {
            setTaxes(prefill.taxes);
        }
        if (prefill.notes) {
            setNotes(prefill.notes);
        }
        setIsEditing(true);
        onPrefillApplied?.();
    }, [prefill]);

    const saveMutation = useMutation({
        mutationFn: async () => {
            const vendorCharges = charges
                .filter(c => c.label.trim())
                .map(c => ({
                    label: c.label.trim(),
                    amount: parseFloat(c.amount) || 0,
                    chargeType: c.chargeType,
                }));
            const taxEntries = taxes
                .filter(t => t.label.trim())
                .map(t => ({
                    label: t.label.trim(),
                    rate: t.rate ? parseFloat(t.rate) : undefined,
                    amount: parseFloat(t.amount) || 0,
                }));

            const res = await authenticatedFetch(`/api/v1/admin/pos/${poId}/shipping-charges`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ vendorCharges, taxes: taxEntries, notes }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to save shipping charges');
            }
            return res.json();
        },
        onSuccess: async (body) => {
            const updated = body?.data;
            if (updated) {
                queryClient.setQueryData(['admin-po-details', poId], updated);
            }
            await queryClient.invalidateQueries({ queryKey: ['admin-po-details', poId] });
            await queryClient.invalidateQueries({ queryKey: ['admin-offers'] });
            await queryClient.invalidateQueries({ queryKey: ['admin-offer-detail'] });
            setIsEditing(false);
            showToast('Shipping charges saved. Customer has been notified.', 'success');
        },
        onError: (err: Error) => showToast(err.message, 'error'),
    });

    const itemsSubtotal = useMemo(() => computeItemsSubtotal(po), [po]);
    const chargesSubtotal = useMemo(
        () => charges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0),
        [charges]
    );
    const taxTotal = useMemo(
        () => taxes.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0),
        [taxes]
    );

    // Recompute tax amounts from stored rates using items subtotal (not charges).
    useEffect(() => {
        setTaxes((prev) => {
            let changed = false;
            const next = prev.map((t) => {
                const rateVal = parseFloat(t.rate);
                if (!Number.isFinite(rateVal) || t.rate === '') return t;
                const amount = ((itemsSubtotal * rateVal) / 100).toFixed(2);
                if (amount === t.amount) return t;
                changed = true;
                return { ...t, amount };
            });
            return changed ? next : prev;
        });
    }, [itemsSubtotal]);

    if (shippingMode === 'self') {
        return (
            <div className="bg-white dark:bg-[hsl(var(--card))] rounded border border-indigo-200 dark:border-indigo-800 shadow-sm overflow-hidden">
                <div className="p-6 bg-indigo-50 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-800">
                    <h2 className="text-lg font-bold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
                        <MdLocalShipping className="w-5 h-5" />
                        Customer Self Shipment
                    </h2>
                    <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-2">
                        The customer is handling logistics with their own partner. You do not need to enter shipping charges.
                        {shippingLocked && ' Shipping mode has been confirmed and locked.'}
                    </p>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Logistic Partner</p>
                        <p className="font-semibold text-[hsl(var(--foreground))]">{details.logisticPartnerName || '—'}</p>
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-[#0088cc]/65 uppercase">AWB / Tracking</p>
                        <p className="font-semibold text-[hsl(var(--foreground))]">{details.awbNumber || details.trackingNumber || '—'}</p>
                    </div>
                </div>
            </div>
        );
    }

    const displayCharges = (details.vendorCharges || []).filter((c: any) => String(c?.label || '').trim());
    const displayTaxes = (details.taxes || []).filter((t: any) => String(t?.label || '').trim());
    const summaryChargesTotal = Number(details.chargesSubtotal ?? chargesSubtotal) || 0;
    const summaryTaxTotal = Number(details.taxTotal ?? taxTotal) || 0;
    const summaryGrand = Number(details.shippingGrandTotal ?? summaryChargesTotal + summaryTaxTotal) || 0;

    if (!isEditing && saved) {
        return (
            <div
                id="shipping-charges"
                className={cn(
                    'bg-white dark:bg-[hsl(var(--card))] rounded border shadow-sm overflow-hidden',
                    highlightCustomerRequest
                        ? 'border-amber-400 ring-2 ring-amber-300/60'
                        : 'border-[#0088cc]/20'
                )}
            >
                {highlightCustomerRequest && (
                    <div className="px-6 py-4 bg-amber-50 border-b border-amber-200 text-amber-950 text-sm">
                        <p className="font-bold">Customer requested shipping charges</p>
                        <p className="mt-1 text-xs">The customer asked you to add shipping charges for this order. Review and enter charges below.</p>
                    </div>
                )}
                <div className="p-6 border-b border-[#0088cc]/20 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
                            <MdLocalShipping className="w-5 h-5 text-[#0088cc]" />
                            Shipping & Charges
                        </h2>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                            Shipping charges have been submitted for this purchase order.
                        </p>
                    </div>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={() => setIsEditing(true)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-[#0088cc] hover:bg-[#0077b3] shrink-0"
                        >
                            <MdEdit className="w-4 h-4" />
                            Edit Shipping Charges
                        </button>
                    )}
                </div>

                <DeliveryAddressCard po={po} />

                <div className="p-6 space-y-6">
                    {displayCharges.length > 0 && (
                        <div>
                            <h3 className="text-sm font-bold text-[hsl(var(--foreground))] uppercase tracking-wider mb-3">Charges</h3>
                            <div className="space-y-2">
                                {displayCharges.map((c: any, idx: number) => (
                                    <div
                                        key={`${c.label}-${idx}`}
                                        className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-[#0088cc]/15 bg-[#0088cc]/5"
                                    >
                                        <div>
                                            <p className="font-semibold text-[hsl(var(--foreground))]">{c.label}</p>
                                            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                                                {c.chargeType || 'custom'}
                                            </p>
                                        </div>
                                        <p className="font-bold text-[hsl(var(--foreground))]">
                                            {po.currency} {Number(c.amount || 0).toFixed(2)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {displayTaxes.length > 0 && (
                        <div>
                            <h3 className="text-sm font-bold text-[hsl(var(--foreground))] uppercase tracking-wider mb-3">Taxes</h3>
                            <div className="space-y-2">
                                {displayTaxes.map((t: any, idx: number) => (
                                    <div
                                        key={`${t.label}-${idx}`}
                                        className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-[#0088cc]/15 bg-[#0088cc]/5"
                                    >
                                        <div>
                                            <p className="font-semibold text-[hsl(var(--foreground))]">{t.label}</p>
                                            {t.rate !== undefined && t.rate !== null && t.rate !== '' && (
                                                <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                                                    Rate {t.rate}%
                                                </p>
                                            )}
                                        </div>
                                        <p className="font-bold text-[hsl(var(--foreground))]">
                                            {po.currency} {Number(t.amount || 0).toFixed(2)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {details.notes && (
                        <div>
                            <h3 className="text-sm font-bold text-[hsl(var(--foreground))] uppercase tracking-wider mb-2">Notes</h3>
                            <p className="text-sm text-[hsl(var(--muted-foreground))] whitespace-pre-wrap">{details.notes}</p>
                        </div>
                    )}

                    <div className="p-4 bg-[#0088cc]/5 rounded-xl border border-[#0088cc]/15 space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-[hsl(var(--muted-foreground))]">Charges subtotal</span>
                            <span className="font-bold text-[hsl(var(--foreground))]">{po.currency} {summaryChargesTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-[hsl(var(--muted-foreground))]">Tax total</span>
                            <span className="font-bold text-[hsl(var(--foreground))]">{po.currency} {summaryTaxTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-[#0088cc]/20 pt-2 font-bold text-[#0088cc]">
                            <span>Sub Total</span>
                            <span>{po.currency} {summaryGrand.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            id="shipping-charges"
            className={cn(
                'bg-white dark:bg-[hsl(var(--card))] rounded border shadow-sm overflow-hidden',
                highlightCustomerRequest
                    ? 'border-amber-400 ring-2 ring-amber-300/60'
                    : 'border-[#0088cc]/20'
            )}
        >
            {highlightCustomerRequest && (
                <div className="px-6 py-4 bg-amber-50 border-b border-amber-200 text-amber-950 text-sm">
                    <p className="font-bold">Customer requested shipping charges</p>
                    <p className="mt-1 text-xs">The customer asked you to add shipping charges for this order. Enter freight, packing, and tax lines below.</p>
                </div>
            )}
            <div className="p-6 border-b border-[#0088cc]/20">
                <h2 className="text-lg font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
                    <MdLocalShipping className="w-5 h-5 text-[#0088cc]" />
                    Shipping & Charges
                </h2>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    Review the customer delivery address, then enter shipping, packing, customs, and any other non-item charges.
                </p>
            </div>

            <DeliveryAddressCard po={po} />

            <div className="p-6 space-y-8">
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-[hsl(var(--foreground))] uppercase tracking-wider">Charges</h3>
                        <button
                            type="button"
                            onClick={() => setCharges([...charges, emptyCharge()])}
                            className="inline-flex items-center gap-1 text-xs font-bold text-[#0088cc] hover:underline"
                        >
                            <MdAdd className="w-4 h-4" /> Add charge
                        </button>
                    </div>
                    <div className="space-y-3">
                        {charges.map((row, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                                <div className="col-span-5">
                                    {idx === 0 && <label className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Label</label>}
                                    <input
                                        type="text"
                                        value={row.label}
                                        onChange={(e) => {
                                            const next = [...charges];
                                            next[idx] = { ...row, label: e.target.value };
                                            setCharges(next);
                                        }}
                                        placeholder="e.g. Shipping Charge, Packing Charge"
                                        className="w-full mt-1 px-3 py-2 border border-[#0088cc]/20 rounded-lg text-sm bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                                        disabled={!canEdit}
                                    />
                                </div>
                                <div className="col-span-3">
                                    {idx === 0 && <label className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Type</label>}
                                    <select
                                        value={row.chargeType}
                                        onChange={(e) => {
                                            const next = [...charges];
                                            next[idx] = { ...row, chargeType: e.target.value as ChargeRow['chargeType'] };
                                            setCharges(next);
                                        }}
                                        className="w-full mt-1 px-3 py-2 border border-[#0088cc]/20 rounded-lg text-sm bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                                        disabled={!canEdit}
                                    >
                                        <option value="shipping">Shipping</option>
                                        <option value="packing">Packing</option>
                                        <option value="custom">Custom</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="col-span-3">
                                    {idx === 0 && <label className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Amount</label>}
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.amount}
                                        onChange={(e) => {
                                            const next = [...charges];
                                            next[idx] = { ...row, amount: e.target.value };
                                            setCharges(next);
                                        }}
                                        placeholder="0.00"
                                        className="w-full mt-1 px-3 py-2 border border-[#0088cc]/20 rounded-lg text-sm bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                                        disabled={!canEdit}
                                    />
                                </div>
                                <div className="col-span-1 flex justify-end">
                                    {charges.length > 1 && canEdit && (
                                        <button
                                            type="button"
                                            onClick={() => setCharges(charges.filter((_, i) => i !== idx))}
                                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg"
                                        >
                                            <MdDelete className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-bold text-[hsl(var(--foreground))] uppercase tracking-wider">Taxes</h3>
                        <button
                            type="button"
                            onClick={() => setTaxes([...taxes, emptyTax()])}
                            className="inline-flex items-center gap-1 text-xs font-bold text-[#0088cc] hover:underline"
                        >
                            <MdAdd className="w-4 h-4" /> Add tax
                        </button>
                    </div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                        Tax is calculated on item total ({po.currency} {itemsSubtotal.toFixed(2)}), not on charges.
                    </p>
                    <div className="space-y-3">
                        {taxes.map((row, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                                <div className="col-span-4">
                                    {idx === 0 && <label className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Tax name</label>}
                                    <input
                                        type="text"
                                        value={row.label}
                                        onChange={(e) => {
                                            const next = [...taxes];
                                            next[idx] = { ...row, label: e.target.value };
                                            setTaxes(next);
                                        }}
                                        placeholder="e.g. GST, VAT"
                                        className="w-full mt-1 px-3 py-2 border border-[#0088cc]/20 rounded-lg text-sm bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                                        disabled={!canEdit}
                                    />
                                </div>
                                <div className="col-span-3">
                                    {idx === 0 && <label className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Rate %</label>}
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.rate}
                                        onChange={(e) => {
                                            const next = [...taxes];
                                            const rateVal = parseFloat(e.target.value) || 0;
                                            const calculatedAmount = (itemsSubtotal * rateVal) / 100;
                                            next[idx] = {
                                                ...row,
                                                rate: e.target.value,
                                                amount: e.target.value === ''
                                                    ? ''
                                                    : calculatedAmount.toFixed(2),
                                            };
                                            setTaxes(next);
                                        }}
                                        placeholder="Optional"
                                        className="w-full mt-1 px-3 py-2 border border-[#0088cc]/20 rounded-lg text-sm bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                                        disabled={!canEdit}
                                    />
                                </div>
                                <div className="col-span-4">
                                    {idx === 0 && <label className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Amount</label>}
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.amount}
                                        onChange={(e) => {
                                            const next = [...taxes];
                                            const amountVal = parseFloat(e.target.value) || 0;
                                            const calculatedRate =
                                                itemsSubtotal > 0 ? (amountVal / itemsSubtotal) * 100 : 0;
                                            next[idx] = {
                                                ...row,
                                                amount: e.target.value,
                                                rate:
                                                    e.target.value === '' || itemsSubtotal <= 0
                                                        ? row.rate
                                                        : calculatedRate.toFixed(2),
                                            };
                                            setTaxes(next);
                                        }}
                                        placeholder="0.00"
                                        className="w-full mt-1 px-3 py-2 border border-[#0088cc]/20 rounded-lg text-sm bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                                        disabled={!canEdit}
                                    />
                                </div>
                                <div className="col-span-1 flex justify-end">
                                    {taxes.length > 1 && canEdit && (
                                        <button
                                            type="button"
                                            onClick={() => setTaxes(taxes.filter((_, i) => i !== idx))}
                                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg"
                                        >
                                            <MdDelete className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-[10px] font-bold text-[#0088cc]/65 uppercase">Notes (optional)</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        className="w-full mt-1 px-3 py-2 border border-[#0088cc]/20 rounded-lg text-sm resize-none bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                        placeholder="Any notes about shipping or charges…"
                        disabled={!canEdit}
                    />
                </div>

                <div className="p-4 bg-[#0088cc]/5 rounded-xl border border-[#0088cc]/15 space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-[hsl(var(--muted-foreground))]">Charges subtotal</span>
                        <span className="font-bold text-[hsl(var(--foreground))]">{po.currency} {chargesSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-[hsl(var(--muted-foreground))]">Tax total</span>
                        <span className="font-bold text-[hsl(var(--foreground))]">{po.currency} {taxTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t border-[#0088cc]/20 pt-2 font-bold text-[#0088cc]">
                        <span>Sub Total</span>
                        <span>{po.currency} {(chargesSubtotal + taxTotal).toFixed(2)}</span>
                    </div>
                </div>

                {canEdit && (
                    <div className="flex flex-col sm:flex-row gap-3">
                        {saved && (
                            <button
                                type="button"
                                onClick={() => setIsEditing(false)}
                                className="flex-1 py-3 rounded-xl font-bold text-sm border border-[#0088cc]/25 text-[hsl(var(--foreground))] hover:bg-[#0088cc]/5"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => saveMutation.mutate()}
                            disabled={saveMutation.isPending}
                            className={cn(
                                'flex-[2] flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-white',
                                'bg-[#0088cc] hover:bg-[#0077b3] disabled:opacity-50'
                            )}
                        >
                            <MdSave className="w-4 h-4" />
                            {saveMutation.isPending ? 'Saving…' : 'Save Shipping Charges'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
